import React, { useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Backdrop,
  Box,
  Typography,
  Button,
  useMediaQuery,
  IconButton,
  LinearProgress,
} from "@mui/material";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import {
  centerStyle,
  centerWideStyle,
  sidePanelStyle,
  toastStyle,
  dialogStyle,
  backdropStyles,
} from "@/utils/theme/modal";
import { useTheme } from "@mui/material/styles";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  onIgnore,
  title = "",
  subtitle = "",
  body = "",
  isProcessing = false,
  processingText = "Loading...",
  showIgnoreButton = true,
  showConfirmButton = true,
  confirmButtonText = "Confirm",
  ignoreButtonText = "Ignore",
  showCloseIcon = false,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  disableSwipeToClose = false,
  allowBackgroundInteraction = false,
  enableDebounce = true,
  debounceMs = 500,
  showSnoozeOption = false,
  onSnooze = () => {},
  modalStyle = "toastStyle",
  backdropStyle = "modern",
  customStyle = {},
  customBackdropStyle = {},
  minHeight = "auto",
  mobileMinHeight = "auto",
  confirmButtonVariant = "text",
  disableConfirm = false,
  invertButtonColors = false,
  snoozeText = "Snooze for 24 hours",
  confirmButtonSx = {},
  ignoreButtonSx = {},
  titleSx = {},
  subtitleSx = {},
  contentSx = {},
  bodySx = {},
  mobileContentSx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const confirmButtonRef = useRef(null);
  const lastCallTime = useRef(0);

  const y = useMotionValue(0);

  const getGreyColor = (shade) => {
    try {
      const color = theme.palette.grey?.[shade];
      return typeof color === "string" ? color : "#999999";
    } catch {
      return "#999999";
    }
  };

  const getModalStyleConfig = () => {
    let baseStyle;
    switch (modalStyle) {
      case "toast":
        baseStyle = toastStyle;
        break;
      case "center":
        baseStyle = centerStyle;
        break;
      case "centerWide":
        baseStyle = centerWideStyle;
        break;
      case "side":
        baseStyle = sidePanelStyle;
        break;
      case "dialog":
        baseStyle = dialogStyle;
        break;
      case "custom":
        baseStyle = customStyle;
        break;
      default:
        baseStyle = toastStyle;
    }

    return modalStyle === "custom"
      ? baseStyle
      : { ...baseStyle, ...customStyle };
  };

  const getBackdropStyleConfig = () => {
    if (Object.keys(customBackdropStyle).length > 0) {
      return customBackdropStyle;
    }
    return backdropStyles[backdropStyle] || backdropStyles.modern;
  };

  const handleConfirm = useCallback(() => {
    if (enableDebounce) {
      const now = Date.now();
      if (now - lastCallTime.current < debounceMs) {
        return;
      }
      lastCallTime.current = now;
    }
    if (onConfirm) {
      onConfirm();
    }
  }, [enableDebounce, debounceMs, onConfirm]);

  const handleIgnore = useCallback(() => {
    if (onIgnore) {
      onIgnore();
    } else {
      onClose();
    }
  }, [onIgnore, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSnooze = useCallback(() => {
    onSnooze();
    onClose();
  }, [onSnooze, onClose]);

  const handleDragEnd = (_, info) => {
    if (disableSwipeToClose) {
      y.set(0);
      return;
    }

    if (info.offset.y > 80 || info.velocity.y > 500) {
      handleClose();
    } else {
      y.set(0);
    }
  };

  useEffect(() => {
    if (open && confirmButtonRef.current) {
      requestAnimationFrame(() => {
        if (document.activeElement) {
          document.activeElement.blur();
        }
        confirmButtonRef.current?.focus();
      });
    }
  }, [open]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!open) return;
      if (e.key === "Enter") {
        const target = e.target;
        const isEditableTarget =
          target instanceof HTMLElement &&
          (target.isContentEditable ||
            ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

        if (isEditableTarget) return;

        e.preventDefault();
        if (onConfirm) {
          handleConfirm();
        }
      }
      if (e.key === "Escape" && !disableEscapeKeyDown) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [open, handleClose, disableEscapeKeyDown, handleConfirm, onConfirm]);

  const desktopContent = (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.15,
        ease: "easeOut",
      }}
      sx={{
        ...getModalStyleConfig(),
        border: "none",
        outline: "none",
        pointerEvents: "auto",
        ...(modalStyle === "sidePanel" && {
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          transform: "none",
          left: "auto",
          width: "100vw",
        }),
      }}
    >
      {isProcessing && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            zIndex: 20,
          }}
        />
      )}
      {showCloseIcon && (
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 10,
            padding: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
      <Box
        sx={{
          width: "100%",
          pt: 5,
          pb: 3,
          px: 3,
          position: "relative",
          backgroundColor: theme.palette.background.paper,
          flex: "0 0 auto",
          display: "flex",
          flexDirection: "column",
          minHeight: minHeight,
          ...contentSx,
        }}
      >
        {title && (
          <Typography
            id="confirm-dialog-title"
            gutterBottom
            sx={{
              mt: 0,
              mb: subtitle ? 1 : 2,
              pr: showCloseIcon ? 5 : 0,
              fontSize: modalStyle === "center" ? "2rem" : "1.375rem",
              fontWeight: 600,
              ...titleSx,
            }}
          >
            {title}
          </Typography>
        )}

        {subtitle && (
          <Typography
            color="textSecondary"
            gutterBottom
            sx={{
              mb: 3,
              pr: showCloseIcon ? 5 : 0,
              ...subtitleSx,
            }}
          >
            {subtitle}
          </Typography>
        )}

        {typeof body === "string" || typeof body === "number" ? (
          <Typography
            variant="body2"
            component="div"
            id="confirm-dialog-description"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: theme.typography.body2.fontSize,
              lineHeight: 1.5,
              flex: 1,
              mb: 3,
              ...bodySx,
            }}
          >
            {body}
          </Typography>
        ) : (
          <Box
            id="confirm-dialog-description"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: theme.typography.body2.fontSize,
              lineHeight: 1.5,
              flex: 1,
              mb: 3,
              ...bodySx,
            }}
          >
            {body}
          </Box>
        )}

        {showSnoozeOption && (
          <Box sx={{ mb: 2 }}>
            <Box
              onClick={handleSnooze}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                color: theme.palette.grey[600],
                transition: "all 0.2s ease",
                "&:hover": {
                  color: theme.palette.primary.main,
                },
              }}
            >
              <NotificationsOffIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  color: theme.palette.grey[400],
                  "&:hover": { color: theme.palette.grey[600] },
                }}
              >
                {snoozeText}
              </Typography>
            </Box>
          </Box>
        )}

        {(showIgnoreButton || showConfirmButton) && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              gap: 1,
              justifyContent: "flex-end",
              height: "40px",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {showIgnoreButton && (
              <Box component={motion.div} whileTap={{ scale: 0.96 }}>
                <Button
                  onClick={handleIgnore}
                  disabled={isProcessing}
                  disableRipple
                  disableFocusRipple
                  variant="text"
                  sx={{
                    minWidth: 0,
                    transition: "all 0.2s",
                    fontWeight: 500,
                    fontSize: "0.9375rem",
                    backgroundColor: "transparent",
                    color: invertButtonColors
                      ? theme.palette.primary.main
                      : theme.palette.grey[400],
                    px: 1.5,
                    height: "40px",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: invertButtonColors
                        ? theme.palette.primary.dark
                        : theme.palette.grey[600],
                    },
                    ...ignoreButtonSx,
                  }}
                >
                  {ignoreButtonText}
                </Button>
              </Box>
            )}
            {showConfirmButton && (
              <Box component={motion.div} whileTap={{ scale: 0.96 }}>
                <Button
                  ref={confirmButtonRef}
                  onClick={handleConfirm}
                  disabled={isProcessing || disableConfirm}
                  disableRipple
                  disableFocusRipple
                  variant={confirmButtonVariant}
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    position: "relative",
                    transition: "all 0.2s",
                    height: "40px",
                    ...(confirmButtonVariant === "contained" && {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.text.primary,
                      px: 3,
                      borderRadius: "8px",
                      ...confirmButtonSx,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      "&:disabled": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.text.primary,
                        opacity: 0.7,
                      },
                    }),
                    ...(confirmButtonVariant === "text" && {
                      backgroundColor: "transparent",
                      color: invertButtonColors
                        ? theme.palette.grey[400]
                        : theme.palette.text.primary,
                      p: 0,
                      "&:hover": {
                        backgroundColor: "transparent",
                        color: invertButtonColors
                          ? theme.palette.grey[600]
                          : theme.palette.text.secondary,
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        color: invertButtonColors
                          ? theme.palette.grey[400]
                          : theme.palette.text.primary,
                        opacity: 0.7,
                      },
                    }),
                  }}
                >
                  {isProcessing ? processingText : confirmButtonText}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );

  // Mobile content
  const mobileContent = (
    <Box
      component={motion.div}
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{
        type: "tween",
        duration: 0.3,
        ease: "easeOut",
      }}
      drag={disableSwipeToClose ? false : "y"}
      dragConstraints={{ top: 0 }}
      dragElastic={0.2}
      onDragEnd={handleDragEnd}
      style={{ y }}
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "10px 10px 0 0",
        maxHeight: "90vh",
        minHeight: mobileMinHeight,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 -10px 30px rgba(0,0,0,0.3)",
        zIndex: 1300,
        pointerEvents: "auto",
        ...mobileContentSx,
      }}
    >
      {isProcessing && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            zIndex: 20,
          }}
        />
      )}
      <Box
        sx={{
          width: 36,
          height: 5,
          backgroundColor: getGreyColor(400),
          borderRadius: 3,
          alignSelf: "center",
          mt: 1.5,
          mb: 1,
          flexShrink: 0,
        }}
      />

      <Box
        sx={{
          width: "100%",
          height: 20,
          flexShrink: 0,
        }}
      />

      <Box
        sx={{
          px: 4,
          pt: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "visible",
        }}
      >
        {showCloseIcon && (
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
              padding: 1.5,
              backgroundColor: getGreyColor(100),
              "&:hover": {
                backgroundColor: getGreyColor(200),
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        {title && (
          <Typography
            variant="h6"
            id="confirm-dialog-title"
            sx={{
              mb: subtitle ? 1 : 1.5,
              pr: showCloseIcon ? 6 : 0,
              fontWeight: 600,
              fontSize: "1.25rem",
            }}
          >
            {title}
          </Typography>
        )}

        {subtitle && (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mb: 4,
              pr: showCloseIcon ? 6 : 0,
            }}
          >
            {subtitle}
          </Typography>
        )}

        <Typography
          variant="body2"
          component="div"
          id="confirm-dialog-description"
          sx={{
            lineHeight: 1.5,
            color: theme.palette.text.secondary,
            opacity: 1,
            fontSize: theme.typography.body2.fontSize,
            flex: 1,
            overflow: "auto",
            mb: showSnoozeOption
              ? 3
              : showIgnoreButton || showConfirmButton
              ? 3
              : 8,
            borderRadius: 0,
          }}
        >
          {body}
        </Typography>

        {showSnoozeOption && (
          <Box sx={{ mb: 3 }}>
            <Box
              onClick={handleSnooze}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                cursor: "pointer",
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: getGreyColor(50),
                },
              }}
            >
              <NotificationsOffIcon
                sx={{
                  fontSize: 24,
                  color: getGreyColor(600),
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  color: getGreyColor(400),
                  "&:hover": { color: getGreyColor(600) },
                }}
              >
                {snoozeText}
              </Typography>
            </Box>
          </Box>
        )}

        {(showIgnoreButton || showConfirmButton) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: showIgnoreButton && showConfirmButton ? 1.5 : 0,
              flexShrink: 0,
              mb: 3,
              mt: showSnoozeOption ? 0 : 3,
            }}
          >
            {showConfirmButton && (
              <Box component={motion.div} whileTap={{ scale: 0.96 }}>
                <Button
                  ref={confirmButtonRef}
                  onClick={handleConfirm}
                  disabled={isProcessing || disableConfirm}
                  disableRipple
                  disableFocusRipple
                  variant={invertButtonColors ? "outlined" : "contained"}
                  sx={{
                    width: "100%",
                    height: 44,
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    ...(invertButtonColors
                      ? {
                          borderColor: getGreyColor(300),
                          color: theme.palette.text.primary,
                          backgroundColor: "transparent",
                          "&:hover": {
                            borderColor: getGreyColor(400),
                            backgroundColor: getGreyColor(50),
                          },
                        }
                      : {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? theme.palette.grey[100]
                              : theme.palette.text.primary,
                          color:
                            theme.palette.mode === "dark"
                              ? theme.palette.text.primary
                              : theme.palette.primary.contrastText,
                          border:
                            theme.palette.mode === "dark"
                              ? `1px solid ${theme.palette.grey[300]}`
                              : "none",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? theme.palette.grey[200]
                                : theme.palette.grey[600],
                            borderColor:
                              theme.palette.mode === "dark"
                                ? theme.palette.grey[400]
                                : "transparent",
                          },
                          "&:disabled": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? theme.palette.grey[100]
                                : theme.palette.text.primary,
                            color:
                              theme.palette.mode === "dark"
                                ? theme.palette.text.primary
                                : theme.palette.primary.contrastText,
                            opacity: 0.7,
                          },
                        }),
                  }}
                >
                  {isProcessing ? processingText : confirmButtonText}
                </Button>
              </Box>
            )}

            {showIgnoreButton && (
              <Box component={motion.div} whileTap={{ scale: 0.96 }}>
                <Button
                  onClick={handleIgnore}
                  disabled={isProcessing}
                  disableRipple
                  disableFocusRipple
                  variant={invertButtonColors ? "contained" : "outlined"}
                  sx={{
                    width: "100%",
                    height: 44,
                    borderRadius: "10px",
                    fontWeight: 600,
                    fontSize: "0.9375rem",
                    ...(invertButtonColors
                      ? {
                          backgroundColor: theme.palette.error.main,
                          "&:hover": {
                            backgroundColor: theme.palette.error.dark,
                          },
                          "&:disabled": {
                            backgroundColor: theme.palette.error.main,
                            color: theme.palette.error.contrastText,
                            opacity: 0.7,
                          },
                        }
                      : {
                          borderColor: getGreyColor(300),
                          color: theme.palette.text.primary,
                          "&:hover": {
                            borderColor: getGreyColor(400),
                            backgroundColor: getGreyColor(50),
                          },
                        }),
                  }}
                >
                  {ignoreButtonText}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );

  useEffect(() => {
    if (open && !allowBackgroundInteraction) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [open, allowBackgroundInteraction]);

  return (
    <Modal
      open={open}
      onClose={disableBackdropClick ? () => {} : handleClose}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      keepMounted={false}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            ...getBackdropStyleConfig(),
            ...(allowBackgroundInteraction && { pointerEvents: "none" }),
          },
        },
      }}
      sx={allowBackgroundInteraction ? { pointerEvents: "none" } : undefined}
    >
      <Box>
        <AnimatePresence mode="wait">
          {open && (isMobile ? mobileContent : desktopContent)}
        </AnimatePresence>
      </Box>
    </Modal>
  );
};

export default ConfirmDialog;
